import { Link as ExpoLink } from 'expo-router';
import * as WebBrowser from 'expo-web-browser';
import React from 'react';
import { GestureResponderEvent, Linking } from 'react-native';

import {
  ButtonProps,
  Pressable,
  Text,
  TextProps,
  LinkProps as UCCLLinkProps,
  addWithConfig,
} from '@fhs-legacy/universal-components';
import { getTextFromChildren } from 'components/action-button';
import useDebounceOnPressCallback from 'hooks/use-debounce-on-press-callback';
import { isExternalLink } from 'utils/is-external-link';

const StyledPressable = Pressable.withConfig<{
  isSelected: boolean;
}>(props => ({
  color: props.isSelected ? Styles.color.tertiary : Styles.color.black,
}));

type IButtonProps = ButtonProps;

interface ILinkSharedProps {
  // Route to navigate with or without state
  to?: string | { pathname: string; params?: any };
  // Path to navigate using linking options
  linkPath?: string;
  // This will open in a new tab
  isExternal?: boolean;
  // Is the link active
  isSelected?: boolean;
  isUnderlined?: boolean;
  // React Router
  replace?: boolean;
  // React Router
  state?: any;
  variant?: TextProps['variant'];
}

export interface IInLineLinkProps extends ILinkSharedProps, TextProps {
  // Is the link used inside of a text block
  isInline: true;
  // Native base had different types
  onPress?: ((event?: GestureResponderEvent) => void) | undefined;
  // TODO: RN WEB - implement hover
  _hover?: Omit<IButtonProps, '_hover'>;
  _text?: TextProps;
}

export interface ILinkProps extends ILinkSharedProps, UCCLLinkProps {
  isInline?: boolean | undefined;
  // Native base had different types
  onPress?: ((event?: GestureResponderEvent) => any) | null | undefined;
}

const openExternalLink = async (URL: string) => {
  try {
    if (!URL.startsWith('http')) {
      await Linking.openURL(URL);
      return;
    }
    await WebBrowser.openBrowserAsync(URL);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn(`Link: there was an error opening the URL: ${URL}`, error);
  }
};

// TODO: RN - add web version of StyledInLineLink
function LinkBase(props: IInLineLinkProps | ILinkProps): React.ReactElement {
  const {
    to,
    linkPath,
    onPress: _onPress,
    children,
    replace,
    state,
    isUnderlined,
    isInline: _isInline,
    isSelected,
    variant,
    _text: textProps,
    ...rest
  } = props;

  const toIsExternal = isExternalLink(typeof to === 'object' ? to.pathname : to);

  const onPress = useDebounceOnPressCallback(
    (event?: GestureResponderEvent) => {
      if (_onPress) {
        _onPress(event);
      }

      if (linkPath) {
        // TODO: I dont understand what this.
        // const openLink = isExternalLink(linkPath) ? openExternalLink : linkTo;
        // return openLink(linkPath);
      }

      if (to) {
        // This is an internal link
        // React Router Native
        if (toIsExternal) {
          openExternalLink(typeof to === 'object' ? to.pathname : to);
          event?.preventDefault();
          return;
        }
      }
    },
    [to, linkPath, _onPress, toIsExternal, state, replace]
  );

  const propsToForward = {
    isUnderlined: Boolean(isUnderlined),
    isSelected: Boolean(isSelected),
    accessibilityRole: to ? 'link' : 'button',
    accessible: true,
    onPress,
    children,
    ...(rest as any),
  } as const;

  const ddActionName = props?.['dd-action-name'] || props?.testID || getTextFromChildren(children);

  const button = (
    <StyledPressable {...propsToForward} dd-action-name={ddActionName}>
      {typeof children === 'string' || typeof children === 'number' ? (
        <Text variant={variant ?? 'copyOne'} underline={isUnderlined} {...textProps}>
          {children}
        </Text>
      ) : (
        children
      )}
    </StyledPressable>
  );

  if (toIsExternal || !to) {
    return button;
  }

  return (
    <ExpoLink href={to} asChild replace={replace}>
      <StyledPressable {...propsToForward} dd-action-name={ddActionName}>
        {typeof children === 'string' || typeof children === 'number' ? (
          <Text variant={variant ?? 'copyOne'} underline={isUnderlined} {...textProps}>
            {children}
          </Text>
        ) : (
          children
        )}
      </StyledPressable>
    </ExpoLink>
  );
}

const Link = addWithConfig(LinkBase);

const secondaryHoverColor = '#000'; // deviates from theme to increase color contrast

export const SecondaryLink = Link.withConfig<{ isSelected?: boolean }>(p => ({
  color: p.isSelected ? secondaryHoverColor : Styles.color.white,
}));

export default Link;
